import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results"]

  connect() {
    this.timeout = null
    this.highlightedIndex = -1
    this._hideResults = this.hideResults.bind(this)
    document.addEventListener("click", this._hideResults)
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.restoreOpenDetails()
  }

  disconnect() {
    document.removeEventListener("click", this._hideResults)
  }

  getCurrentIds() {
    const params = new URLSearchParams(window.location.search)
    return params.getAll("geography_ids[]")
  }

  getCurrentPeriod() {
    const params = new URLSearchParams(window.location.search)
    return params.get("period")
  }

  navigateWithIds(ids, openCard = null, openDetailIdx = null, sortMode = null) {
    if (ids.length === 0) {
      window.location.href = "/"
      return
    }
    const params = new URLSearchParams()
    ids.forEach(id => params.append("geography_ids[]", id))
    const period = this.getCurrentPeriod()
    if (period) params.set("period", period)
    if (openCard) params.set("open_card", openCard)
    if (openDetailIdx != null) params.set("open_detail", openDetailIdx)
    if (sortMode) params.set("sort_mode", sortMode)
    window.location.href = `/dashboard?${params.toString()}`
  }

  onInput() {
    clearTimeout(this.timeout)
    this.highlightedIndex = -1
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.resultsTarget.style.display = "none"
      return
    }

    this.timeout = setTimeout(() => this.search(query), 300)
  }

  onKeydown(event) {
    const items = this.resultsTarget.querySelectorAll(".search-result-item[data-id]")
    if (!items.length || this.resultsTarget.style.display === "none") {
      if (event.key === "Enter") {
        event.preventDefault()
        this.submitSearch()
      }
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1)
      this.updateHighlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
      this.updateHighlight(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this.highlightedIndex >= 0 && items[this.highlightedIndex]) {
        this.addGeography(items[this.highlightedIndex].dataset.id)
      } else {
        this.submitSearch()
      }
    } else if (event.key === "Escape") {
      this.resultsTarget.style.display = "none"
      this.highlightedIndex = -1
    }
  }

  updateHighlight(items) {
    items.forEach((item, i) => {
      item.classList.toggle("highlighted", i === this.highlightedIndex)
    })
    if (items[this.highlightedIndex]) {
      items[this.highlightedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  get fetchHeaders() {
    const headers = { "Accept": "application/json" }
    if (this.csrfToken) headers["X-CSRF-Token"] = this.csrfToken
    return headers
  }

  async search(query) {
    try {
      const response = await fetch(`/api/geographies/search?q=${encodeURIComponent(query)}`, {
        headers: this.fetchHeaders,
        credentials: "same-origin"
      })
      if (!response.ok) return
      const results = await response.json()
      this.highlightedIndex = -1
      this.renderResults(results)
    } catch (error) {
      console.error("Search error:", error)
    }
  }

  renderResults(results) {
    const currentIds = this.getCurrentIds()

    if (results.length === 0) {
      this.resultsTarget.innerHTML = '<div class="search-result-item no-results">No results found</div>'
      this.resultsTarget.style.display = "block"
      return
    }

    // Group by geo_type, preserving order (MSA > City > Zip)
    const groups = {}
    const typeOrder = []
    results.forEach(r => {
      if (!groups[r.geo_type]) {
        groups[r.geo_type] = []
        typeOrder.push(r.geo_type)
      }
      groups[r.geo_type].push(r)
    })

    let html = ""
    typeOrder.forEach(type => {
      const label = groups[type][0].geo_type_label || type.toUpperCase()
      html += `<div class="search-group-header">${label}</div>`

      groups[type].forEach(r => {
        const alreadySelected = currentIds.includes(String(r.id))
        html += `
          <div class="search-result-item ${alreadySelected ? "already-selected" : ""}"
               data-action="click->search#selectResult"
               data-id="${r.id}">
            <span class="geo-type-badge geo-type-${r.geo_type}">${r.geo_type_label || r.geo_type.toUpperCase()}</span>
            ${r.display_name}
            ${alreadySelected ? '<span class="already-badge">Added</span>' : ""}
          </div>
        `
      })
    })

    this.resultsTarget.innerHTML = html
    this.resultsTarget.style.display = "block"
  }

  selectResult(event) {
    const id = event.currentTarget.dataset.id
    const card = event.currentTarget.closest("[data-geography-id]")
    const openCard = card ? card.dataset.geographyId : null

    // Track which <details> dropdown was open so we reopen the right one
    let openDetailIdx = null
    let sortMode = null
    if (card) {
      const details = event.currentTarget.closest("details")
      if (details) {
        const allDetails = Array.from(card.querySelectorAll("details"))
        openDetailIdx = allDetails.indexOf(details)

        // Capture active sort mode so we can restore it after navigation
        const activeSort = details.querySelector(".report-sort-btn.active")
        if (activeSort) sortMode = activeSort.dataset.sort
      }
    }

    this.addGeography(id, openCard, openDetailIdx, sortMode)
  }

  addGeography(id, openCard = null, openDetailIdx = null, sortMode = null) {
    const currentIds = this.getCurrentIds()
    if (currentIds.includes(String(id))) return
    if (currentIds.length >= 5) {
      this.showMaxNotice()
      return
    }

    const newId = String(id)
    let newIds

    // Insert adjacent to the card that triggered the add
    if (openCard) {
      const idx = currentIds.indexOf(String(openCard))
      if (idx !== -1) {
        newIds = [...currentIds]
        newIds.splice(idx + 1, 0, newId)
      } else {
        newIds = [...currentIds, newId]
      }
    } else {
      newIds = [...currentIds, newId]
    }

    this.navigateWithIds(newIds, openCard, openDetailIdx, sortMode)
  }

  toggleGeography(event) {
    const chip = event.currentTarget
    const index = parseInt(chip.dataset.index, 10)
    const isActive = !chip.classList.contains("geo-chip-inactive")
    chip.classList.toggle("geo-chip-inactive", isActive)
    document.dispatchEvent(new CustomEvent("toggle-geography", {
      detail: { index, visible: !isActive }
    }))
  }

  removeGeography(event) {
    event.stopPropagation()
    const idToRemove = String(event.currentTarget.dataset.id)
    const currentIds = this.getCurrentIds()
    this.navigateWithIds(currentIds.filter(id => id !== idToRemove))
  }

  clearAll() {
    window.location.href = "/"
  }

  submitSearch() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) return

    fetch(`/api/geographies/search?q=${encodeURIComponent(query)}`, {
      headers: this.fetchHeaders,
      credentials: "same-origin"
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(results => {
        if (results.length > 0) {
          this.addGeography(String(results[0].id))
        }
      })
      .catch(err => console.error("Search error:", err))
  }

  hideResults(event) {
    if (!this.element.contains(event.target)) {
      this.resultsTarget.style.display = "none"
      this.highlightedIndex = -1
    }
  }

  restoreOpenDetails() {
    const params = new URLSearchParams(window.location.search)
    const openCard = params.get("open_card")
    if (!openCard) return

    const card = document.querySelector(`[data-geography-id="${openCard}"]`)
    if (card) {
      const detailIdx = parseInt(params.get("open_detail"), 10)
      const allDetails = card.querySelectorAll("details")
      let openedDetails = null
      if (!isNaN(detailIdx) && allDetails[detailIdx]) {
        allDetails[detailIdx].open = true
        openedDetails = allDetails[detailIdx]
      } else if (allDetails[0]) {
        allDetails[0].open = true
        openedDetails = allDetails[0]
      }

      // Restore sort mode (e.g. "pop") on the reopened details section
      const sortMode = params.get("sort_mode")
      if (sortMode && openedDetails) {
        const sortBtn = openedDetails.querySelector(`.report-sort-btn[data-sort="${sortMode}"]`)
        if (sortBtn && typeof sortChildList === "function") {
          sortChildList(sortBtn, sortMode)
        }
      }
    }

    // Clean up the URL params without triggering navigation
    params.delete("open_card")
    params.delete("open_detail")
    params.delete("sort_mode")
    const clean = params.toString()
    const url = clean ? `/dashboard?${clean}` : "/dashboard"
    history.replaceState(null, "", url)
  }

  showMaxNotice() {
    const existing = document.querySelector(".max-geo-notice")
    if (existing) existing.remove()

    const notice = document.createElement("div")
    notice.className = "max-geo-notice"
    notice.textContent = "Maximum of 5 areas reached. Remove one to add another."
    document.body.appendChild(notice)

    requestAnimationFrame(() => notice.classList.add("visible"))
    setTimeout(() => {
      notice.classList.remove("visible")
      setTimeout(() => notice.remove(), 300)
    }, 3000)
  }
}
